import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Divider, 
  IconButton, 
  Tooltip, 
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  ContentCopy as ContentCopyIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Translate as TranslateIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import TimeSavedIndicator from '../TimeSavedIndicator';
import { Terminology as TerminologyType } from '../../services/contentService';

interface TerminologyProps {
  terms: TerminologyType[];
  timeSaved: number;
  videoTitle: string;
  isSaved?: boolean;
  onSave?: () => void;
  onCopy?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
}

const Terminology: React.FC<TerminologyProps> = ({
  terms,
  timeSaved,
  videoTitle,
  isSaved = false,
  onSave = () => {},
  onCopy = () => {},
  onShare = () => {},
  onDownload = () => {},
  onPrint = () => {}
}) => {
  // State for more options menu
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const moreMenuOpen = Boolean(moreAnchorEl);
  
  // State for search query
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for view mode
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');
  
  // Handle more menu open
  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };
  
  // Handle more menu close
  const handleMoreMenuClose = () => {
    setMoreAnchorEl(null);
  };
  
  // Handle copy
  const handleCopy = () => {
    const content = terms.map(term => 
      `Term: ${term.term}\nDefinition: ${term.definition}\nContext: ${term.context || 'N/A'}\n\n`
    ).join('');
    
    navigator.clipboard.writeText(content);
    onCopy();
  };
  
  // Handle save
  const handleSave = () => {
    onSave();
  };
  
  // Handle share
  const handleShare = () => {
    onShare();
    handleMoreMenuClose();
  };
  
  // Handle download
  const handleDownload = () => {
    // Create content string
    const content = terms.map(term => 
      `# ${term.term}\n\nDefinition: ${term.definition}\n\nContext: ${term.context || 'N/A'}\n\n---\n\n`
    ).join('');
    
    // Create a blob with the content
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    // Create a link and click it
    const a = document.createElement('a');
    a.href = url;
    a.download = `${videoTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_terminology.txt`;
    document.body.appendChild(a);
    a.click();
    
    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    onDownload();
    handleMoreMenuClose();
  };
  
  // Handle print
  const handlePrint = () => {
    // Create content HTML
    const contentHtml = terms.map(term => 
      `<div class="term">
        <h2>${term.term}</h2>
        <div class="definition">
          <h3>Definition:</h3>
          <p>${term.definition}</p>
        </div>
        <div class="context">
          <h3>Context:</h3>
          <p>${term.context || 'No context available'}</p>
        </div>
      </div>`
    ).join('');
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${videoTitle} - Terminology</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                margin: 20px;
                color: #333;
              }
              h1 {
                font-size: 24px;
                margin-bottom: 20px;
              }
              .time-saved {
                color: #06d6a0;
                font-weight: bold;
                margin-bottom: 20px;
              }
              .term {
                margin-bottom: 30px;
                page-break-inside: avoid;
                border: 1px solid #ddd;
                border-radius: 8px;
                padding: 15px;
              }
              h2 {
                font-size: 20px;
                margin-top: 0;
                margin-bottom: 15px;
                color: #00b4d8;
              }
              h3 {
                font-size: 16px;
                margin-top: 0;
                margin-bottom: 5px;
                color: #555;
              }
              .definition, .context {
                margin-bottom: 15px;
              }
              @media print {
                body {
                  font-size: 12pt;
                }
                h1 {
                  font-size: 18pt;
                }
                h2 {
                  font-size: 16pt;
                }
                h3 {
                  font-size: 14pt;
                }
                .term {
                  page-break-inside: avoid;
                  border: 1px solid #999;
                }
              }
            </style>
          </head>
          <body>
            <h1>${videoTitle} - Terminology</h1>
            <div class="time-saved">Saved ${timeSaved} minutes</div>
            ${contentHtml}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
    
    onPrint();
    handleMoreMenuClose();
  };
  
  // Handle search change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  
  // Handle view mode change
  const handleViewModeChange = (mode: 'cards' | 'list') => {
    setViewMode(mode);
  };
  
  // Filter terms based on search query
  const filteredTerms = terms.filter(term => {
    const query = searchQuery.toLowerCase();
    return (
      term.term.toLowerCase().includes(query) ||
      term.definition.toLowerCase().includes(query) ||
      (term.context ? term.context.toLowerCase().includes(query) : false)
    );
  });
  
  // Sort terms alphabetically
  const sortedTerms = [...filteredTerms].sort((a, b) => 
    a.term.localeCompare(b.term)
  );
  
  // Render cards view
  const renderCardsView = () => (
    <Grid container spacing={2}>
      {sortedTerms.map((term, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card 
            elevation={0}
            sx={{ 
              height: '100%',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              bgcolor: 'rgba(0, 180, 216, 0.05)',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                transform: 'translateY(-2px)',
                bgcolor: 'rgba(0, 180, 216, 0.08)',
              }
            }}
          >
            <CardContent>
              <Typography 
                variant="h6" 
                component="h3" 
                gutterBottom
                sx={{ 
                  color: 'primary.main',
                  fontWeight: 600
                }}
              >
                {term.term}
              </Typography>
              
              <Typography 
                variant="subtitle2" 
                gutterBottom
                sx={{ 
                  color: 'text.secondary',
                  mb: 1
                }}
              >
                Definition:
              </Typography>
              
              <Typography 
                variant="body2" 
                paragraph
                sx={{ 
                  mb: 2
                }}
              >
                {term.definition}
              </Typography>
              
              <Typography 
                variant="subtitle2" 
                gutterBottom
                sx={{ 
                  color: 'text.secondary',
                  mb: 1
                }}
              >
                Context:
              </Typography>
              
              <Typography 
                variant="body2" 
                color="text.secondary"
              >
                {term.context || 'No context available'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
  
  // Render list view
  const renderListView = () => (
    <Box>
      {sortedTerms.map((term, index) => (
        <Accordion 
          key={index}
          elevation={0}
          sx={{
            bgcolor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
            mb: 1,
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: '8px !important',
            overflow: 'hidden',
            '&.Mui-expanded': {
              margin: '0 0 8px 0',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`term-${index}-content`}
            id={`term-${index}-header`}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.02)',
              '& .MuiAccordionSummary-content': {
                margin: '12px 0',
              },
            }}
          >
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600
              }}
            >
              {term.term}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              p: 3,
              borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            }}
          >
            <Typography 
              variant="subtitle2" 
              gutterBottom
              sx={{ 
                color: 'text.secondary',
                mb: 1
              }}
            >
              Definition:
            </Typography>
            
            <Typography 
              variant="body2" 
              paragraph
              sx={{ 
                mb: 2
              }}
            >
              {term.definition}
            </Typography>
            
            <Typography 
              variant="subtitle2" 
              gutterBottom
              sx={{ 
                color: 'text.secondary',
                mb: 1
              }}
            >
              Context:
            </Typography>
            
            <Typography 
              variant="body2" 
              color="text.secondary"
            >
              {term.context || 'No context available'}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
  
  return (
    <Paper 
      elevation={0} 
      className="glass-container"
      sx={{ 
        p: 3,
        mb: 4
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TranslateIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h2">
            Terminology Simplifier
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TimeSavedIndicator minutes={timeSaved} />
          
          <Box sx={{ display: 'flex', ml: 2 }}>
            <Tooltip title={isSaved ? "Saved to your library" : "Save to your library"}>
              <IconButton onClick={handleSave} color={isSaved ? "primary" : "default"}>
                {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Copy to clipboard">
              <IconButton onClick={handleCopy}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="More options">
              <IconButton
                onClick={handleMoreMenuOpen}
                aria-label="more options"
                aria-controls="more-menu"
                aria-haspopup="true"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Simplified explanations of technical terms and complex concepts from the video.
      </Typography>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <TextField
          placeholder="Search terms..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: '100%', sm: 300 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 4,
            },
          }}
        />
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Card view">
            <Chip
              icon={<FilterListIcon />}
              label="Cards"
              clickable
              color={viewMode === 'cards' ? 'primary' : 'default'}
              variant={viewMode === 'cards' ? 'filled' : 'outlined'}
              onClick={() => handleViewModeChange('cards')}
              sx={{ borderRadius: 4 }}
            />
          </Tooltip>
          
          <Tooltip title="List view">
            <Chip
              icon={<FilterListIcon />}
              label="List"
              clickable
              color={viewMode === 'list' ? 'primary' : 'default'}
              variant={viewMode === 'list' ? 'filled' : 'outlined'}
              onClick={() => handleViewModeChange('list')}
              sx={{ borderRadius: 4 }}
            />
          </Tooltip>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      {/* Terms count */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {filteredTerms.length} {filteredTerms.length === 1 ? 'term' : 'terms'} found
          {searchQuery && ` for "${searchQuery}"`}
        </Typography>
      </Box>
      
      {/* Terms content */}
      {filteredTerms.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No terms match your search query.
          </Typography>
        </Box>
      ) : (
        viewMode === 'cards' ? renderCardsView() : renderListView()
      )}
      
      {/* More options menu */}
      <Menu
        id="more-menu"
        anchorEl={moreAnchorEl}
        open={moreMenuOpen}
        onClose={handleMoreMenuClose}
        MenuListProps={{
          'aria-labelledby': 'more-button',
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleShare}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePrint}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default Terminology;
